/**
 * 路由预加载优化工具
 */

import { imagePreloader } from './imagePreloader.js'

class RoutePreloader {
  constructor() {
    this.preloadedRoutes = new Set()
    this.preloadingRoutes = new Set()
  }

  // 预加载路由组件
  async preloadRoute(routeName) {
    if (this.preloadedRoutes.has(routeName) || this.preloadingRoutes.has(routeName)) {
      return
    }

    this.preloadingRoutes.add(routeName)

    try {
      // 根据路由名称动态导入组件
      const routeComponentMap = {
        'About': () => import('../views/About.vue'),
        'Services': () => import('../views/Services.vue'),
        'Countries': () => import('../views/Countries.vue'),
        'Testimonials': () => import('../views/Testimonials.vue'),
        'Parents': () => import('../views/Parents.vue'),
        'News': () => import('../views/News.vue'),
        'Policies': () => import('../views/Policies.vue'),
        'Practice': () => import('../views/Practice.vue'),
        'Blog': () => import('../views/blog.vue'),
        'BlogDetail': () => import('../views/BlogDetail.vue')
      }

      const componentLoader = routeComponentMap[routeName]
      if (componentLoader) {
        // 并行加载组件和图片
        await Promise.all([
          componentLoader(),
          imagePreloader.preloadPageImages(routeName)
        ])
        
        this.preloadedRoutes.add(routeName)
        console.log(`路由 ${routeName} 预加载完成`)
      }
    } catch (error) {
      console.warn(`路由 ${routeName} 预加载失败:`, error)
    } finally {
      this.preloadingRoutes.delete(routeName)
    }
  }

  // 预加载多个路由
  async preloadRoutes(routeNames, options = {}) {
    const { concurrent = 2 } = options
    
    const chunks = []
    for (let i = 0; i < routeNames.length; i += concurrent) {
      chunks.push(routeNames.slice(i, i + concurrent))
    }

    for (const chunk of chunks) {
      await Promise.allSettled(
        chunk.map(routeName => this.preloadRoute(routeName))
      )
    }
  }

  // 智能预加载：根据用户行为预测下一个可能访问的页面
  getNextLikelyRoutes(currentRoute) {
    const routeFlow = {
      'Home': ['About', 'Services', 'Countries'],
      'About': ['Services', 'Testimonials'],
      'Services': ['Countries', 'Parents'],
      'Countries': ['Services', 'Testimonials'],
      'Testimonials': ['Parents', 'About'],
      'Parents': ['Services', 'Policies'],
      'Blog': ['BlogDetail'],
      'News': ['Blog']
    }

    return routeFlow[currentRoute] || []
  }

  // 启动智能预加载
  startIntelligentPreloading(currentRoute) {
    const nextRoutes = this.getNextLikelyRoutes(currentRoute)
    
    // 延迟预加载，避免影响当前页面性能
    setTimeout(() => {
      this.preloadRoutes(nextRoutes, { concurrent: 1 })
    }, 2000)
  }

  // 获取预加载状态
  getPreloadStatus() {
    return {
      preloaded: Array.from(this.preloadedRoutes),
      preloading: Array.from(this.preloadingRoutes)
    }
  }
}

// 创建全局实例
export const routePreloader = new RoutePreloader()

// Vue插件
export const RoutePreloaderPlugin = {
  install(Vue) {
    Vue.prototype.$routePreloader = routePreloader

    // 全局混入，在路由变化时启动智能预加载
    Vue.mixin({
      async beforeRouteEnter(to, from, next) {
        next(vm => {
          if (vm && to.name) {
            // 延迟启动智能预加载
            vm.$nextTick(() => {
              routePreloader.startIntelligentPreloading(to.name)
            })
          }
        })
      }
    })
  }
}

export default routePreloader
